/**
 * Panel event queue for announce-before-show gating.
 *
 * OpenAI Realtime emits visual tool-call events
 * before or in parallel with the audio that should announce them, causing panels
 * to pop up before the user hears the explanation. This module holds deferred
 * visual events until the next `turn_complete` / `interrupted` signal, with a
 * configurable fallback timer in case neither signal arrives.
 *
 * Pure module — no DOM, no window, no side effects at module load time. (#58)
 */

/** A panel-display event emitted by a realtime model tool call. */
export type PanelEvent = {
  kind: string;
  payload: unknown;
};

/**
 * Deferred event kinds — the realtime model emits these as side-effects of
 * tool calls, and we hold them until the conversational seam (turn_complete /
 * interrupted) so the caller hears the announce sentence before the panel
 * paints. Tool→event mapping (server prompt names → client event kinds):
 *   show_offers              → show_offers
 *   show_example_builds      → show_example_builds
 *   generate_workflow_diagram → workflow_diagram
 *   show_savings             → show_savings
 *   show_contact             → show_contact
 *   lookup_faq               → show_briefing_book
 *   confirm_briefing         → booking
 * Renaming a tool or event requires touching both the server prompt
 * (system-prompt.ts ANNOUNCE BEFORE SHOWING block) and this list.
 */
const DEFERRED_KINDS_ARRAY = [
  'show_offers',
  'show_example_builds',
  'show_contact',
  'show_savings',
  'workflow_diagram',
  'show_briefing_book',
  'booking',
  'brief_submitted',
] as const;

export const DEFERRED_KINDS: ReadonlySet<string> = new Set<string>(
  DEFERRED_KINDS_ARRAY,
);

/**
 * Returns `true` when `kind` belongs to the deferred-display set.
 * Use this to decide whether to `enqueue` rather than render immediately.
 */
export function isDeferredKind(kind: string): boolean {
  return DEFERRED_KINDS.has(kind);
}

/** Options for constructing a {@link PanelQueue}. */
export interface PanelQueueOptions {
  /**
   * How many milliseconds to wait before auto-flushing if neither
   * `turn_complete` nor `interrupted` arrives. Defaults to 4000.
   */
  fallbackMs?: number;
}

/**
 * FIFO queue that holds deferred panel events until explicitly flushed or until
 * the fallback timer fires.
 *
 * Typical lifecycle:
 * 1. Wire up via `setFlushHandler`.
 * 2. On each deferred `tool_call` frame: `enqueue(event)`.
 * 3. On `turn_complete` or `interrupted`: `flush()`.
 *
 * @example
 * ```ts
 * const q = new PanelQueue({ fallbackMs: 4000 });
 * q.setFlushHandler(evt => showPanel(evt.kind, evt.payload));
 * // … later …
 * q.enqueue({ kind: 'show_offers', payload: offersData });
 * // … on turn_complete …
 * q.flush();
 * ```
 */
export class PanelQueue {
  private readonly fallbackMs: number;
  private handler: ((e: PanelEvent) => void) | null = null;
  private queue: PanelEvent[] = [];
  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor(opts?: PanelQueueOptions) {
    this.fallbackMs = opts?.fallbackMs ?? 4000;
    // Bind flush explicitly so the timer callback always has the right `this`.
    this.flush = this.flush.bind(this);
  }

  /**
   * Register (or replace) the function that will be called for each event
   * during a flush. May be called multiple times to rebind.
   */
  setFlushHandler(fn: (e: PanelEvent) => void): void {
    this.handler = fn;
  }

  /**
   * Add an event to the back of the queue and (re)start the fallback timer.
   * If a timer was already running it is cancelled and restarted so the
   * deadline is always relative to the most-recent enqueue.
   */
  enqueue(event: PanelEvent): void {
    this.queue.push(event);
    this.resetFallback();
  }

  /**
   * Drain the queue in FIFO order, calling the registered flush handler for
   * each event. Handler exceptions are caught and logged so a single bad event
   * cannot abort delivery of subsequent events. Cancels the fallback timer
   * regardless of whether the queue was empty.
   */
  flush(): void {
    this.cancelFallback();
    const events = this.queue.splice(0);
    for (const event of events) {
      try {
        this.handler?.(event);
      } catch (err) {
        console.error('[PanelQueue] flush handler threw for event:', event.kind, err);
      }
    }
  }

  /**
   * Drop all queued events without dispatching them and cancel the fallback
   * timer. Useful when a session resets or the user navigates away.
   */
  clear(): void {
    this.cancelFallback();
    this.queue.splice(0);
  }

  // ─── private helpers ────────────────────────────────────────────────────────

  private resetFallback(): void {
    this.cancelFallback();
    this.timerId = setTimeout(this.flush, this.fallbackMs);
  }

  private cancelFallback(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
